/**
 * Path predicates for maintenance mode (TX-4910).
 *
 * Same shape and reasoning as `routeAccess.ts`: a pure module with no imports,
 * so every gate shares one allow-list, the middleware bundle stays small, and
 * the list is unit-testable on its own.
 *
 * Shared out of `apps/web` in tranche 2a-iii (TX-4989): `apps/api` needs the
 * same list, and two copies of "who stays online during an outage" is not a
 * thing to keep in step by hand.
 *
 * Paths here keep working while maintenance is on. Everything else gets a 503.
 */

/**
 * Prefixes that stay up during maintenance, and why each one has to.
 *
 * - `/admin`, `/api/v1/admin` — admins are never locked out; also where the
 *   "maintenance is live" banner lives.
 * - `/landing`, `/sso-callback`, `/sso-google-callback`, `/accept-token` — an
 *   admin who is signed out still has to be able to sign in. This is the
 *   safety exemption to "everything except admin", not a scope change.
 *
 *   `/landing` and not `/sign-in`: there is no `/sign-in` route and there has
 *   not been since launch — it answers 404. The entry named it anyway, so this
 *   exemption protected nothing, while `/landing` — the page that actually
 *   renders the sign-in form, via `LandingContent` — was not on the list and
 *   took the 503. A signed-out admin was locked out for the entire life of the
 *   feature.
 *
 *   The cost is deliberate and worth stating: an anonymous visitor can reach
 *   the marketing page during a window instead of the maintenance notice. The
 *   app itself is still behind the 503 — `/`, the dashboard and every member
 *   surface — so what stays reachable is the public page that has always been
 *   safe to serve, and the alternative is having no way back in.
 * - `/api/v1/webhooks` — Stripe and Clerk retries are finite. Dropping them
 *   loses subscription and identity state for real.
 * - `/api/v1/cron` — Vercel crons.
 * - `/api/health` — the liveness probe. A 503 here reads as "the deployment is
 *   broken", which is not what a planned window means.
 */
const MAINTENANCE_EXEMPT_PREFIXES = [
  "/admin",
  "/api/v1/admin",
  "/landing",
  "/sso-callback",
  "/sso-google-callback",
  "/accept-token",
  "/api/v1/webhooks",
  "/api/v1/cron",
  "/api/health",
] as const;

/**
 * True when `pathname` keeps working with maintenance on.
 *
 * Matches on **segment boundaries**, not bare `startsWith`. A plain prefix
 * match would exempt `/api/v1/administrivia` along with `/api/v1/admin`, so any
 * future route whose name happens to start with an exempt one would silently
 * escape maintenance. This list decides who stays online; it does not get to be
 * approximate.
 */
export function isMaintenanceExemptPath(pathname: string): boolean {
  return MAINTENANCE_EXEMPT_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

/**
 * True for requests that expect JSON rather than a page.
 *
 * Drives the shape of the 503 only: an API caller gets `{"error":"maintenance"}`,
 * a browser gets the HTML page. Both carry `Retry-After`.
 */
export function isMaintenanceApiPath(pathname: string): boolean {
  return pathname.startsWith("/api/");
}

/** Query param that carries the bypass secret; also the cookie name. */
export const MAINTENANCE_BYPASS_PARAM = "maintenance-bypass";
export const MAINTENANCE_BYPASS_COOKIE = "sukha-maintenance-bypass";

/**
 * `Retry-After`, in seconds. An hour: long enough that crawlers back off,
 * short enough that they come back the same day. Keep real windows under a
 * day — a 503 that persists for days does start to affect indexing.
 */
export const MAINTENANCE_RETRY_AFTER_SECONDS = 3600;

/**
 * How long a resolved maintenance state may be reused before re-evaluating.
 *
 * Shared because BOTH hosts read the switch: `apps/web` from routing
 * middleware, `apps/api` from its own gate (TX-4990). Without a cache it was
 * read on every page view and every API call — measured at 2.4M billed flag
 * requests in a month, to answer a question whose answer is "off" essentially
 * always. Thirty seconds makes the bill scale with how often the switch is
 * *flipped* rather than with traffic. Since TX-5250 the switch is an Edge
 * Config item (`maintenanceSwitch.ts`), and the same window applies.
 *
 * The cost is latency on a flip: up to this long for a warm instance to notice,
 * in either direction. That is the right trade for a planned window, and the
 * `MAINTENANCE_MODE` environment override stays instant for the case where
 * Vercel itself is the thing that is degraded.
 *
 * One number, so the two hosts cannot come out of maintenance at different
 * times — which would look exactly like a partial outage.
 */
export const MAINTENANCE_CACHE_TTL_MS = 30_000;
